<template>
  <v-app>
    <!-- Header -->
    <v-app-bar color="primary" dark>
      <v-app-bar-nav-icon @click="drawer = !drawer"></v-app-bar-nav-icon>
      <v-toolbar-title class="text-left">{{ getPageTitle }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon>
        <v-icon>mdi-bell</v-icon>
      </v-btn>
      
      <!-- Account Menu -->
      <v-menu>
        <template v-slot:activator="{ props }">
          <v-btn icon v-bind="props">
            <v-icon>mdi-account</v-icon>
          </v-btn>
        </template>
        <v-list min-width="200">
          <v-list-item>
            <v-list-item-title class="font-weight-medium">
              <v-icon icon="mdi-account-circle" size="20" class="mr-2"></v-icon>
              Admin User
            </v-list-item-title>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item @click="handleProfile">
            <v-list-item-title>
              <v-icon icon="mdi-account-edit" size="20" class="mr-2"></v-icon>
              Hồ sơ cá nhân
            </v-list-item-title>
          </v-list-item>
          <v-list-item @click="handleSettings">
            <v-list-item-title>
              <v-icon icon="mdi-cog" size="20" class="mr-2"></v-icon>
              Cài đặt
            </v-list-item-title>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item @click="handleLogout" class="logout-item">
            <v-list-item-title class="text-red">
              <v-icon icon="mdi-logout" size="20" class="mr-2 text-red"></v-icon>
              Đăng xuất
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <!-- Sidebar -->
    <v-navigation-drawer v-model="drawer" app>
      <v-list>
        <v-list-item
          v-for="item in menuItems"
          :key="item.title"
          :to="item.path"
          :prepend-icon="item.icon"
          :title="item.title"
          @click="handleMenuClick"
        ></v-list-item>
      </v-list>
    </v-navigation-drawer>

    <!-- Main Content -->
    <v-main>
      <v-container fluid>
        <router-view />
      </v-container>
    </v-main>

    <!-- Logout Confirmation Dialog -->
    <v-dialog v-model="logoutDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon icon="mdi-logout" class="mr-2"></v-icon>
          Xác nhận đăng xuất
        </v-card-title>
        <v-card-text>
          Bạn có chắc chắn muốn đăng xuất khỏi hệ thống?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn 
            text 
            @click="logoutDialog = false"
            color="grey"
          >
            Hủy
          </v-btn>
          <v-btn 
            color="red" 
            variant="elevated"
            @click="confirmLogout"
          >
            Đăng xuất
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useDisplay } from 'vuetify'
import { useRouter, useRoute } from 'vue-router'

const drawer = ref(true)
const logoutDialog = ref(false)
const router = useRouter()
const route = useRoute()

const menuItems = [
  { title: 'Dashboard', icon: 'mdi-view-dashboard', path: '/admin/dashboard' },
  { title: 'Phân tích', icon: 'mdi-chart-line', path: '/admin/analysis' },
  { title: 'Người dùng', icon: 'mdi-account', path: '/admin/user' },
  { title: 'Danh mục', icon: 'mdi-folder', path: '/admin/category' },
  { title: 'Sản phẩm', icon: 'mdi-package', path: '/admin/product' },
  { title: 'Đăng tự động', icon: 'mdi-robot', path: '/admin/auto-post' },
  { title: 'Đơn hàng', icon: 'mdi-cart', path: '/admin/order' },
  { title: 'Media', icon: 'mdi-image', path: '/admin/media' },
  { title: 'Feedback', icon: 'mdi-comment-multiple', path: '/admin/feedback' },
  {title: 'Giấy phép', icon: 'mdi-certificate', path: '/admin/license'},
  { title: 'Mã giảm giá', icon: 'mdi-ticket-percent', path: '/admin/promo' },
  { title: 'Affiliate', icon: 'mdi-account-group', path: '/admin/affiliate' },
  { title: 'Rút tiền', icon: 'mdi-bank-transfer', path: '/admin/withdrawal' },
  { title: 'Danh mục Quiz', icon: 'mdi-help-circle-outline', path: '/admin/quiz-category' },
  { title: 'Câu hỏi Quiz', icon: 'mdi-help-circle', path: '/admin/quiz-question' },
  { title: 'Cài đặt', icon: 'mdi-cog', path: '/admin/setting' },
]

const { smAndDown } = useDisplay()

// Map route names to Vietnamese titles
const pageTitles = {
  'Dashboard': 'Bảng điều khiển',
  'User': 'Quản lý người dùng',
  'Category': 'Quản lý danh mục',
  'Product': 'Quản lý sản phẩm',
  'UpdateProduct': 'Cập nhật sản phẩm',
  'AutoPost': 'Đăng sản phẩm tự động',
  'Order': 'Quản lý đơn hàng',
  'Media': 'Quản lý Media',
  'Feedback': 'Quản lý Feedback',
  'Promo': 'Quản lý mã giảm giá',
  'Affiliate': 'Quản lý Affiliate',
  'Withdrawal': 'Quản lý rút tiền',
  'QuizCategory': 'Quản lý danh mục Quiz',
  'QuizQuestion': 'Quản lý câu hỏi Quiz',
  'Analysis': 'Phân tích chi tiêu quảng cáo',
  'Setting': 'Cài đặt hệ thống'
}

const getPageTitle = computed(() => {
  return pageTitles[route.name] || route.name || 'Admin Panel'
})

function handleMenuClick() {
  if (smAndDown.value) {
    drawer.value = false
  }
}

function handleProfile() {
  // TODO: Navigate to profile page
  console.log('Navigate to profile')
}

function handleSettings() {
  router.push('/admin/setting')
}

function handleLogout() {
  // Show confirmation dialog
  logoutDialog.value = true
}

function confirmLogout() {
  // Clear token from localStorage
  localStorage.removeItem('token')
  
  // Close dialog
  logoutDialog.value = false
  
  // Redirect to login page
  router.push('/login')
}
</script>

<style scoped>
.v-navigation-drawer {
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}

/* Account Menu Styling */
.logout-item {
  transition: background-color 0.2s ease;
}

.logout-item:hover {
  background-color: rgba(244, 67, 54, 0.08) !important;
}

.text-red {
  color: #f44336 !important;
}

/* Account Menu item hover effects */
.v-menu .v-list-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

/* Account menu user info item (first item) - non-clickable */
.v-menu .v-list-item:first-child {
  pointer-events: none;
  background-color: #f5f5f5;
}

/* Sidebar menu items text alignment */
:deep(.v-navigation-drawer .v-list-item-title) {
  text-align: left !important;
}
</style>